import React, { useEffect, useRef, useState } from 'react';
import { Formik, Form, Field, FormikHelpers, FormikErrors } from 'formik';
import { Redirect } from 'react-router-dom';
import { Colors } from 'src/common';
import { merchantLoginRoute, paymentsDashboardRoute } from 'src/routing/routes';
import { FloatingInput, ShapesDecorativeBorder, DotLoader } from 'src/components';
import TealiumEvents from 'src/helpers/tealium-events';
import { MiniAppFocusWrapper } from 'src/components/miniapp-focus-wrapper/miniapp-focus-wrapper';
import { SelfServiceFormBackground } from '../self-service-form-background/self-service-form-background';
import toastMessageServiceSingleton from 'src/services/toast-message.service';
import { FormContainer } from '../form-container/form-container';
import { ImageCache } from 'src/components/image-cache/image-cache';
import {
  CactusImage,
  CashAdvanceApproved,
  CashAdvanceCongrats,
  CashAdvanceRoadSign,
  ComingSoonLabel,
  Error,
  FallenIceCream,
  FallingBags,
  HandCash,
  InputErrorIcon,
  RocketImage,
  TapPay,
  TickedCheckboxPortal,
  UntickedCheckboxPortal,
  CloseIcon,
} from 'src/assets/images';
import useTechnicalErrorHandler from 'src/hooks/useTechnicalError';
import MiniAppEvents from 'src/helpers/miniapp-events';
import { Button } from '@vfs-digital-channels/ns-react-components';
import {
  LoginTypeLookUpResponse,
  merchantKey,
  merchantAuth,
  MerchantLoginRequestBody,
  LoginResponseMessages,
} from 'src/services/ni-login';

type LoginFormValues = {
  merchantId: string;
  password: string;
};

export default function loginMerchant() {
  const inMiniProgram = MiniAppEvents.isInMiniApp();
  const handleTechnicalError = useTechnicalErrorHandler();
  const [loginType, setLoginType] = useState<LoginTypeLookUpResponse | null>(null);
  const [disableMerchantId, setDisableMerchantId] = useState<boolean>(false);
  const [redirectToRoute, setRedirectToRoute] = useState('');
  const [initialValues, setInitialValues] = useState<LoginFormValues>({
    merchantId: '',
    password: '',
  });
  const topOfPage = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (loginType) {
      setDisableMerchantId(true);
      setInitialValues({
        merchantId: loginType.user ?? '',
        password: '',
      });
    }
  }, [loginType]);

  useEffect(() => {
    const storedResponse = sessionStorage.getItem('loginTypeResponse');
    if (storedResponse) {
      setLoginType(JSON.parse(storedResponse));
    }

    if (MiniAppEvents.isInMiniApp()) {
      const siteTitle = document.getElementById('siteTitle');
      if (siteTitle) {
        siteTitle.innerText = 'Business Portal';
      }
    }
  }, []);

  useEffect(() => {
    if (inMiniProgram) {
      MiniAppEvents.sendMessage({
        messageType: 'get_msisdn',
      });

      MiniAppEvents.listenForMessage((observer) => {
        const message = MiniAppEvents.readMessage();
        if (message.type === 'get_msisdn') {
          if (message.data) {
            const newValues = { ...initialValues, merchantId: message.data };
            setInitialValues(newValues);
          }
          observer.disconnect();
        }
      });
    }
  }, [inMiniProgram]);

  useEffect(() => {
    TealiumEvents.loginPageLoadNewStyle();
  }, []);

  if (!!redirectToRoute) {
    return <Redirect push to={redirectToRoute} />;
  }

  const formContent = () => {
    return (
      <section className="self-service-login-form">
        <ImageCache
          images={[
            TapPay,
            CactusImage,
            CashAdvanceApproved,
            FallingBags,
            HandCash,
            CashAdvanceCongrats,
            CashAdvanceRoadSign,
            ComingSoonLabel,
            Error,
            FallenIceCream,
            InputErrorIcon,
            RocketImage,
            UntickedCheckboxPortal,
            TickedCheckboxPortal,
            CloseIcon,
          ]}
        />
        <Formik<LoginFormValues>
          initialValues={initialValues}
          validate={validateLoginForm}
          onSubmit={submitLoginForm}
          validateOnChange={true}
          validateOnBlur={false}
          enableReinitialize={true}
        >
          {({ isSubmitting, values }) => (
            <FormContainer>
              <Form>
                <div className="pt-10 text-center">
                  <div className="pt-5 pb-4 text-4xl text-grey-600" ref={topOfPage}>
                    Log in to your account
                  </div>
                </div>
                <div className="pb-8 space-y-8">
                  <div className="space-y-5">
                    <Field
                      name="merchantId"
                      type="text"
                      placeholder="Merchant ID"
                      component={MiniAppFocusWrapper}
                      innerComponent={FloatingInput}
                      fieldType="standard"
                      className="top-field"
                      spacing="has-margin-top-5"
                      automationIdPrefix="Login_Input_MerchantID"
                      disabled={disableMerchantId}
                      autoFocus
                    />
                    <Field
                      name="password"
                      type="password"
                      component={MiniAppFocusWrapper}
                      innerComponent={FloatingInput}
                      className="bottom-field"
                      fieldType="standard"
                      placeholder="Password"
                      automationIdPrefix="Login_Input_Password"
                    />
                  </div>
                  <div className="space-y-[10px]">
                    <Button
                      className="w-full !py-6 !text-base !font-medium !bg-red-600 disabled:!bg-grey-100"
                      type="submit"
                      disabled={isSubmitting || !isFormValid(values)}
                    >
                      {isSubmitting ? <DotLoader color={Colors.BlueGray4} /> : 'Login'}
                    </Button>
                    <Button
                      className="w-full !py-6  !text-base text-black !font-medium rounded-lg border border-grey-100 !bg-white-50"
                      onClick={() => handleBackButton()}
                    >
                      Back
                    </Button>
                  </div>
                </div>
              </Form>
            </FormContainer>
          )}
        </Formik>
      </section>
    );
  };

  return (
    <div className="broker-login">
      <SelfServiceFormBackground className="">
        <ShapesDecorativeBorder>{formContent()}</ShapesDecorativeBorder>
      </SelfServiceFormBackground>
    </div>
  );

  function handleSuccessfullLogin(values: LoginFormValues) {
    TealiumEvents.loginClickLogin(values.merchantId);
    setRedirectToRoute(paymentsDashboardRoute);
  }
  function handleBackButton() {
    setRedirectToRoute(merchantLoginRoute);
    sessionStorage.removeItem(merchantKey.merchantLookUpRespondeKey);
  }

  function handleBadLogin() {
    TealiumEvents.onHandledErrorFormSelfService('Invalid Merchant ID or password', '401');
    toastMessageServiceSingleton.addError(
      'Invalid Merchant ID or password',
      null,
      '401',
      true,
      null,
      true,
    );
  }

  async function submitLoginForm(values: LoginFormValues, helpers: FormikHelpers<LoginFormValues>) {
    const { setSubmitting } = helpers;
    const params = {
      ...values,
      showToastOnError: false,
      suppressTealiumEvent: true,
    };
    const requestBody: MerchantLoginRequestBody = {
      merchantEnrollment: false,
      username: params.merchantId,
      password: btoa(params.password),
      findMerchantRequestId: '',
      findMerchantResponseId: '',
    };
    try {
      const response = await merchantAuth(requestBody);

      setSubmitting(false);
      if (response === LoginResponseMessages.WRONG_CREDENTIALS) {
        handleBadLogin();
      } else {
        handleSuccessfullLogin(values);
      }
    } catch (error) {
      // @ts-ignore
      handleTechnicalError('unrecognized error received in response to login POST request');
    }
  }
}

const isFormValid = (values) => values.merchantId && values.password;

function validateLoginForm(values) {
  const errors: FormikErrors<LoginFormValues> = {};
  if (!values.merchantId) {
    errors.merchantId = 'Your merchant ID is incorrect';
  }

  if (!values.password) {
    errors.password = 'Your password is incorrect';
  }
  return errors;
}
